import { useState, useRef } from "react"
import { useForm } from "react-hook-form"
import { PasswordRequirementsTooltip } from "@/modules/shared/ui/components/password-requirements-tooltip"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/modules/shared/ui/components/button"
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/modules/shared/ui/components/card"
import { Input } from "@/modules/shared/ui/components/input"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/modules/shared/ui/components/form"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { Link } from "react-router-dom"

const formSchema = z
    .object({
        newPassword: z
            .string()
            .min(8, "Password must be at least 8 characters")
            .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
            .regex(/[a-z]/, "Password must contain at least one lowercase letter")
            .regex(/[0-9]/, "Password must contain at least one number")
            .regex(
                /[!@#$%^&*]/,
                "Password must contain at least one special character (!@#$%^&*)"
            ),
        confirmPassword: z.string().min(1, {
            message: "Please confirm your password.",
        }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords do not match.",
        path: ["confirmPassword"],
    })

type FormValues = z.infer<typeof formSchema>

interface ResetPasswordFormProps {
    onSubmit: (newPassword: string) => Promise<void>
    isLoading?: boolean
    errorMessage?: string
    hasValidToken?: boolean
    isCoolingDown?: boolean
    email?: string
}

export default function ResetPasswordForm({
    onSubmit,
    isLoading = false,
    errorMessage = "",
    hasValidToken = true,
    isCoolingDown = false,
    email = "",
}: ResetPasswordFormProps) {
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            newPassword: "",
            confirmPassword: "",
        },
    })

    const submittingRef = useRef(false)

    const handleSubmit = async (values: FormValues) => {
        if (submittingRef.current) return
        submittingRef.current = true
        try {
            await onSubmit(values.newPassword)
        } finally {
            submittingRef.current = false
        }
    }

    if (!hasValidToken) {
        return (
            <div className="w-full md:w-[75%] justify-center items-center">
                <Card className="shadow-none border-0 w-full">
                    <CardHeader>
                        <CardTitle className="text-3xl">Invalid Reset Link</CardTitle>
                        <CardDescription className="text-base text-muted-foreground">
                            This password reset link is invalid or has expired.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <p className="text-muted-foreground">
                                Please request a new password reset link.
                            </p>
                            <Link
                                to="/forgot-password"
                                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white rounded-md"
                                style={{ backgroundColor: "#1A237E" }}
                            >
                                Request New Link
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="w-full md:w-[75%] justify-center items-center">
            <Card className="shadow-none border-0 w-full">
                <CardHeader>
                    <CardTitle className="text-3xl">Reset your password</CardTitle>
                    <CardDescription className="text-base text-muted-foreground">
                        {email
                            ? <>Enter your new password for <span className="font-medium text-foreground">{email}</span>.</>
                            : "Enter your new password below."}
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
                            <FormField
                                control={form.control}
                                name="newPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium flex items-center gap-1.5">
                                            <span>New Password</span>
                                            <PasswordRequirementsTooltip />
                                        </FormLabel>
                                        <FormControl>
                                            <div className="relative">
                                                <Input
                                                    type={showPassword ? "text" : "password"}
                                                    placeholder="Enter new password"
                                                    className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                                                    {...field}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                                                >
                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                            </div>
                                        </FormControl>
                                        <FormMessage className="text-red-500" />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-foreground font-medium">
                                            Confirm Password
                                        </FormLabel>
                                        <FormControl>
                                            <div className="relative">
                                                <Input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    placeholder="Confirm new password"
                                                    className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                                                    {...field}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                                                >
                                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                            </div>
                                        </FormControl>
                                        <FormMessage className="text-red-500" />
                                    </FormItem>
                                )}
                            />

                            {errorMessage && (
                                <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
                            )}

                            <Button
                                type="submit"
                                className="w-full text-white py-6 text-base"
                                style={{ backgroundColor: "#1A237E" }}
                                disabled={isLoading || isCoolingDown}
                            >
                                {isCoolingDown
                                    ? "Try again later"
                                    : isLoading
                                        ? "Resetting..."
                                        : "Reset password"}
                            </Button>

                            <div className="text-center">
                                <Link
                                    to="/login"
                                    className="text-sm font-medium hover:underline"
                                    style={{ color: "#1A237E" }}
                                >
                                    Back to login
                                </Link>
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    )
}
